import {
  mapToData, DataModel, SerializationContext, SerializedData, cloneMap, isSameMap, isSameData, DataModelBuilder,
} from '../dto/dto.js';

import { MapUtils } from '../../utility/map-utils.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { Price } from './price.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';

export class DynamicPricingSettings extends DataModel {
  static readonly TYPE_ID: string = 'd4fe2ff26d774a22b382e7d395e33b91';

  constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string,
    public readonly priceModifiers: Map<string, Price>,
  ) {
    super();
  }

  get $typeId() {
    return DynamicPricingSettings.TYPE_ID;
  }

  static empty(businessRef: BusinessModuleRef, eventId: string): DynamicPricingSettings {
    return new DynamicPricingSettings(businessRef, eventId, new Map<string, Price>());
  }

  static fromMap(context: SerializationContext, map: SerializedData): DynamicPricingSettings {
    return new DynamicPricingSettings(
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'eventId'),
        MapUtils.getMap<string, Price>(map, 'priceModifiers', (k, v) => {
          return { key: k, value: Price.decode(v as string) };
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'priceModifiers': mapToData(this.priceModifiers, (k, v) => {
        return { key: k, value: v.encode() };
      }),
    };
  }

  clone(): this {
    return new DynamicPricingSettings(
        this.businessRef.clone(),
        this.eventId,
        cloneMap(this.priceModifiers, (v) => v.clone()),
    ) as this;
  }

  isSame(other: DynamicPricingSettings): boolean {
    if(this === other) {
      return true;
    }

    return isSameMap(other.priceModifiers, this.priceModifiers, isSameData) &&
        isSameData(other.businessRef, this.businessRef) &&
        other.eventId === this.eventId;
  }

  scaleTicketPrice(ticketId: string, price: Price): Price {
    const modifier = this.priceModifiers.get(ticketId);
    if(isNotNullOrUndefined(modifier)) {
      return price.add(modifier);
    }

    return price;
  }
}

export class DynamicPricingSettingsBuilder extends DataModelBuilder<DynamicPricingSettings> {
  build(context: SerializationContext, map: SerializedData): DynamicPricingSettings {
    return DynamicPricingSettings.fromMap(context, map);
  }
}
